import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Formatters, Messages } from '../../../gen-src/Localize';
import { FlexShrink0 } from '../../styles/Flexbox';
import { TabStyles } from '../../styles/TabStyles';
import { isCtrl } from '../../utils/BrowserDetection';
import { findBlurRelatedTarget, generateId } from '../../utils/DomUtil';
import { LiveAnnouncer } from '../../utils/LiveAnnouncer';
import { mergeEventHandlers, ProhibitPropsFrom, PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { DropdownPositionedAboveContext } from '../Dropdown/DropdownOverlay';
import { TextFieldProps, TextFieldWidget } from '../TextField/TextFieldWidget';
import { List, ListIsActiveContext, ListItem, ListProps } from './List';
import { findFocusableItemIndex, findIndexOfItemWithValue, isSelectableItem } from './ListItemUtils';
import { FilteringListStyleName } from './ListStyles';

export { ListItem };
export const FilteringListMessages: Pick<typeof Messages, 'noMatches' | 'optionCountAnnouncement'> = Object.freeze({
  noMatches: Messages.noMatches,
  optionCountAnnouncement: Messages.optionCountAnnouncement,
});

export type FilteringListProps = Partial<Pick<TextFieldProps, 'label' | 'labelId'>> & ProhibitPropsFrom<ListProps, 'selectOnEnterKey'> & {
  /** In the case that you wish to provide `aria-label`, this prop must be set to `''`. */
  'aria-labelledby'?: string;
  /** Applied to the `<input>` element, useful primarily for avoiding heuristics in Chrome. */
  autoComplete?: string;
  /** Focus the search field on mount. */
  autoFocus?: boolean;
  /** When provided, will be rendered between the search field and the list */
  children?: React.ReactNode;
  inputTestId?: string;
  listId?: string;
  listTestId?: string;
  /**
   * Called when the user (or a higher component) wishes to filter `items` with a new string.
   * As with any controlled input field, `searchText` should be updated with `value`.
   */
  onFilterChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchText?: string;
};

export interface FilteringListState {
  activeItemIndex: number;
  focusWithin: boolean;
  width?: number;
}

const FieldWrapperStyleName = TypeStyle.style({
  ...FlexShrink0,
  padding: TabStyles.Spacing.SmallSpace,
});
const FieldStyle: React.CSSProperties = {
  width: '100%',
};

/**
 * Use `FilteringList` when it's necessary to have custom filtering logic, e.g. when the filtering is done server-side, or for custom formatting
 * of the items based on filtering results. Otherwise most cases will probably just want `LocallyFilteringList`.
 */
export class FilteringList extends React.PureComponent<PropsAndElementAttrs<FilteringListProps>, FilteringListState> {
  private announceTimeoutId: number = 0;
  private readonly defaultListId: string = generateId();
  private listId = () => this.props.listId || this.defaultListId;
  private readonly defaultLabelId: string = generateId();
  private labelId = () => this.props.labelId || this.defaultLabelId;
  private listRef: React.RefObject<List> = React.createRef();
  private inputElement: HTMLInputElement | null;

  public state: FilteringListState = {
    activeItemIndex: -1,
    focusWithin: false,
    width: undefined,
  };

  public componentDidUpdate(prevProps: FilteringListProps, prevState: FilteringListState): void {
    const { items } = this.props;
    const { activeItemIndex, focusWithin } = this.state;

    if (prevProps.items !== items) {
      if (focusWithin) {
        this.announceOptionCount();
      }

      // preserve active item after filtering
      const activeItemValue = prevProps.items?.[prevState.activeItemIndex]?.value;
      this.setState({ activeItemIndex: findIndexOfItemWithValue(items, activeItemValue) });
    }

    if (prevState.activeItemIndex !== activeItemIndex) {
      this.listRef.current!.setActiveItemIndex(activeItemIndex);
    }
  }

  public componentDidMount(): void {
    this.listRef.current!.setActiveItemIndex(this.state.activeItemIndex);
    window.setTimeout(() => {
      const firstSelectedItemIndex = findIndexOfItemWithValue(this.props.items, this.getFirstSelectedValue());
      this.listRef.current?.scrollItemIndexIntoView(firstSelectedItemIndex);

      if (this.props.autoFocus) {
        this.inputElement?.focus();
      }
    });
  }

  public componentWillUnmount(): void {
    window.clearTimeout(this.announceTimeoutId);
  }

  /** Gets the index into `props.items` of the pseudo-focused listbox option. */
  public getActiveItemIndex(): number {
    return this.state.activeItemIndex;
  }

  /** Debounced announcement of option count, to avoid reading a new count with every keystroke while the user is still typing. */
  private announceOptionCount(timeout: number = 2000): void {
    window.clearTimeout(this.announceTimeoutId); // it's a safe noop to call clearTimeout on an invalid timer id

    const optionCount = this.props.items.filter(isSelectableItem).length;
    const formattedOptionCount = Formatters.formatCount(optionCount);
    this.announceTimeoutId = window.setTimeout(() =>
      LiveAnnouncer.announce(FilteringListMessages.optionCountAnnouncement({ optionCount, formattedOptionCount }), 'assertive'),
      timeout
    );
  }

  private getFirstSelectedValue(): string | undefined {
    // this seems stupid but actually it's terrible
    // https://salesforce-internal.slack.com/archives/CB5KQ7Y31/p1667425583470669
    return (this.props.selectedValues?.values().next().value as string | undefined) || this.props.selectedValue || undefined;
  }

  private handleBlur: React.FocusEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    if (!e.currentTarget.contains(findBlurRelatedTarget(e))) {
      this.setState({ activeItemIndex: -1, focusWithin: false });
    }
  }, e => this.props.onBlur?.(e));

  private handleFocusCapture: React.FocusEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    this.setState({ focusWithin: true });
    if (this.inputElement && e.target !== this.inputElement) {
      this.inputElement.focus();
      this.setState({ activeItemIndex: this.listRef.current!.getActiveItemIndex() }); // don't let it get out of sync
    }
  }, e => this.props.onFocusCapture?.(e));

  private clearInput = (): void => {
    this.props.onFilterChange?.('');
    this.setState({ activeItemIndex: -1 });
  }
  private handleInputChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    this.setState({ activeItemIndex: -1 }); // reset aria-activedescendant when filtering
    this.props.onFilterChange?.(e.target.value);
  }

  private handleInputFocus = () => this.announceOptionCount(5000);
  private handleInputBlur = () => window.clearTimeout(this.announceTimeoutId);

  private moveSelection(start: number, direction: -1 | 1): void {
    this.setState({ activeItemIndex: findFocusableItemIndex(this.props.items, start, direction) });
  }

  private keyUpHandlers: { [key: string]: React.KeyboardEventHandler } = {};
  private handleKeyUp: React.KeyboardEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    if (this.keyUpHandlers[e.key]) {
      this.keyUpHandlers[e.key](e);
      delete this.keyUpHandlers[e.key];
      e.stopPropagation();
      e.preventDefault();
    }
  }, e => this.props.onKeyUp?.(e));

  private keyDownHandlers: { [key: string]: (e: React.KeyboardEvent<HTMLElement>) => void | boolean } = Object.freeze({
    'Ctrl+a': () => {
      if (this.state.activeItemIndex > -1 && this.props.selectedValues && this.props.onSelectAll) {
        this.props.onSelectAll();
        return false;
      }
      return true;
    },
    'Ctrl+Home': () => this.moveSelection(-1, +1),
    'Ctrl+End': () => this.moveSelection(this.props.items.length, -1),
    'ArrowUp': () => this.moveSelection(this.state.activeItemIndex, -1),
    'ArrowDown': () => this.moveSelection(this.state.activeItemIndex, +1),
    'Enter': () => {
      if (this.state.activeItemIndex > -1 && this.props.onSelect) {
        const activeItem = this.props.items[this.state.activeItemIndex];
        if (isSelectableItem(activeItem)) {
          this.props.onSelect?.(activeItem.value!);
        }
      }
    },
    'Escape': e => {
      if (this.state.activeItemIndex > -1) {
        if (!e.repeat) {
          this.keyUpHandlers[e.key] = () => this.setState({ activeItemIndex: -1 });
        }
        return false;
      }
      return true;
    }
  });
  private handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    const key = (isCtrl(e) ? 'Ctrl+' : '') + e.key;
    if (this.keyDownHandlers[key]) {
      if (!this.keyDownHandlers[key](e)) { // handler returning falsy means the event was handled
        e.stopPropagation();
        e.preventDefault();
      }
    }
  }, e => this.props.onKeyDown?.(e));

  private handleDivRef = (ref: HTMLDivElement | null): void => {
    if (!ref) {
      this.setState({ width: undefined });
    } else {
      // pin the width to prevent the widget from shrinking when items are filtered
      window.setTimeout(() => {
        if (document.body.contains(ref)) {
          this.setState({ width: Math.ceil(ref.getBoundingClientRect().width) });
        }
      });
    }
  }

  public render(): React.ReactNode {
    // eslint-disable-next-line max-len
    const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby, autoComplete, autoFocus, children, className, listTestId, inputTestId, items, label, labelId, listId, onFilterChange, onSelect, onSelectAll, searchPlaceholder, searchText, selectOnEnterKey, selectedValue, selectedValues, style, testId, ...htmlAttrs } = this.props;
    const { activeItemIndex, focusWithin, width } = this.state;

    return <DropdownPositionedAboveContext.Consumer>{reverse =>
      <div
        {...htmlAttrs}
        className={TypeStyle.classes(FilteringListStyleName, reverse && 'reverse', className)}
        data-tb-test-id={testId}
        onBlur={this.handleBlur}
        onFocusCapture={this.handleFocusCapture}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleKeyUp}
        ref={this.handleDivRef}
        style={{ width, ...style }}
      >
        <TextFieldWidget
          aria-activedescendant={activeItemIndex > -1 ? `${this.listId()}-${activeItemIndex}` : undefined}
          aria-autocomplete='list'
          aria-controls={this.listId()}
          aria-expanded // since our pattern doesn't actually use a popup, this makes the most sense
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby ?? this.labelId()}
          autoComplete={autoComplete}
          className={FieldWrapperStyleName}
          kind='search'
          label={label}
          labelId={this.labelId()}
          onBlur={this.handleInputBlur}
          onChange={this.handleInputChange}
          onClear={this.clearInput}
          onFocus={this.handleInputFocus}
          placeholder={searchPlaceholder}
          ref={this.updateInputRef}
          role='combobox'
          style={FieldStyle}
          testId={inputTestId!}
          value={searchText || ''}
        />
        {children}
        <ListIsActiveContext.Provider value={focusWithin && activeItemIndex > -1}>
          <List
            // ARIA WG agrees that this is duplicative but requires it nonetheless 😞 https://github.com/w3c/aria/issues/1725
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby ?? this.labelId()}
            id={this.listId()}
            items={items}
            onSelect={onSelect}
            ref={this.listRef}
            {...(selectedValues ? { selectedValues, onSelectAll } : { selectedValue })}
            selectOnEnterKey={false}
            tabIndex={-1}
            testId={listTestId}
          />
        </ListIsActiveContext.Provider>
      </div>
    }</DropdownPositionedAboveContext.Consumer>;
  }

  private updateInputRef = (ref: HTMLInputElement | null): void => {
    this.inputElement = ref;
  }
}
